import { createApi } from '@reduxjs/toolkit/query/react';
import { BackendResponse } from '../../types';
import { parseQueryParams } from '../../utils';
import { baseQueryWithReauth } from './baseQuery';

export enum AdvancedPaymentTableConfigPage {
  LISTING = 'LISTING',
  APPROVALS = 'APPROVALS',
  REPORTS = 'REPORTS',
}

export interface AdvancedPaymentColumnConfigItem {
  key: string;
  label: string;
  visible: boolean;
  order: number;
}

export interface CreateAdvancedPaymentTableConfigDto {
  page: AdvancedPaymentTableConfigPage;
  name: string;
  fieldConfigJson: {
    data: AdvancedPaymentColumnConfigItem[];
  };
}

export interface UpdateAdvancedPaymentTableConfigDto {
  page: AdvancedPaymentTableConfigPage;
  name: string;
  fieldConfigJson: {
    data: AdvancedPaymentColumnConfigItem[];
  };
}

export interface ApplyAdvancedPaymentTableConfigDto {
  configId: number;
  page: AdvancedPaymentTableConfigPage;
}

export interface AdvancedPaymentTableConfig {
  id: number;
  page?: AdvancedPaymentTableConfigPage;
  name: string;
  configName?: string;
  fieldConfigJson?: {
    data: AdvancedPaymentColumnConfigItem[];
  };
  columns?: AdvancedPaymentColumnConfigItem[];
  isDefault?: boolean;
  createdAt?: string;
  updatedAt?: string;
  createdByUser?: {
    ID: number;
    FullName: string;
    EmailID: string;
  };
}

const advancedPaymentTableConfigApi = createApi({
  reducerPath: 'advancedPaymentTableConfig',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['AdvancedPaymentTableConfig'],
  endpoints: builder => ({
    getAdvancedPaymentTableConfigList: builder.query<
      BackendResponse<AdvancedPaymentTableConfig[]>,
      void
    >({
      query: () => ({ url: '/advanced-payment-table-config', method: 'GET' }),
      providesTags: ['AdvancedPaymentTableConfig'],
    }),

    getActiveAdvancedPaymentTableConfig: builder.query<
      BackendResponse<AdvancedPaymentTableConfig>,
      { page: AdvancedPaymentTableConfigPage }
    >({
      query: ({ page }) => ({
        url: `/advanced-payment-table-config/active${parseQueryParams({ page })}`,
        method: 'GET',
      }),
      providesTags: ['AdvancedPaymentTableConfig'],
    }),

    getAdvancedPaymentTableConfig: builder.query<
      BackendResponse<AdvancedPaymentTableConfig>,
      { id: number }
    >({
      query: ({ id }) => ({
        url: `/advanced-payment-table-config/${id}`,
        method: 'GET',
      }),
      providesTags: (result, error, { id }) => [
        { type: 'AdvancedPaymentTableConfig', id },
      ],
    }),

    createAdvancedPaymentTableConfig: builder.mutation<
      BackendResponse<AdvancedPaymentTableConfig>,
      CreateAdvancedPaymentTableConfigDto
    >({
      query: payload => ({
        url: '/advanced-payment-table-config',
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: ['AdvancedPaymentTableConfig'],
    }),

    updateAdvancedPaymentTableConfig: builder.mutation<
      BackendResponse<AdvancedPaymentTableConfig>,
      { id: number; data: UpdateAdvancedPaymentTableConfigDto }
    >({
      query: ({ id, data }) => ({
        url: `/advanced-payment-table-config/${id}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['AdvancedPaymentTableConfig'],
    }),

    deleteAdvancedPaymentTableConfig: builder.mutation<
      BackendResponse<void>,
      { id: number }
    >({
      query: ({ id }) => ({
        url: `/advanced-payment-table-config/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['AdvancedPaymentTableConfig'],
    }),

    applyAdvancedPaymentTableConfig: builder.mutation<
      BackendResponse<void>,
      ApplyAdvancedPaymentTableConfigDto
    >({
      query: payload => ({
        url: '/advanced-payment-table-config/apply',
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: ['AdvancedPaymentTableConfig'],
    }),
  }),
});

export const {
  useGetAdvancedPaymentTableConfigListQuery,
  useGetActiveAdvancedPaymentTableConfigQuery,
  useGetAdvancedPaymentTableConfigQuery,
  useCreateAdvancedPaymentTableConfigMutation,
  useUpdateAdvancedPaymentTableConfigMutation,
  useDeleteAdvancedPaymentTableConfigMutation,
  useApplyAdvancedPaymentTableConfigMutation,
} = advancedPaymentTableConfigApi;

export default advancedPaymentTableConfigApi;
